import { Check } from "lucide-react";
import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal } from "@/components/bits";
import CTASection from "@/components/CTASection";
import { SEO, PROFILE, INDUSTRIES, EXECUTIVES, IMAGES } from "@/lib/site";

const WhoWeHelp = () => {
    useSEO({ title: SEO["/who-we-help"].title, description: SEO["/who-we-help"].description, path: "/who-we-help" });

    return (
        <>
            <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20" data-testid="who-hero">
                <div className="bg-grid-light absolute inset-0 [mask-image:radial-gradient(70%_60%_at_35%_20%,black,transparent)]" aria-hidden="true" />
                <div className="container-x relative">
                    <Eyebrow>Who We Help</Eyebrow>
                    <Masked
                        lines={["Built for Companies That Already", "Have Momentum — and Know There", "Is More Profit to Capture."]}
                        className="mt-7 max-w-5xl font-display text-4xl font-extrabold leading-[1.06] tracking-tightest sm:text-6xl lg:text-[64px]"
                    />
                    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slategray sm:text-xl">
                        Vantera is designed for established B2B organizations that already have customers, sales infrastructure, market demand, and meaningful operating spend.
                    </p>
                </div>
            </section>

            <section className="pb-24 sm:pb-32" data-testid="who-profile">
                <div className="container-x grid items-center gap-14 lg:grid-cols-2">
                    <Reveal className="relative order-2 lg:order-1">
                        <div className="overflow-hidden rounded-2xl">
                            <img src={IMAGES.whoWeHelp} alt="Industrial distribution warehouse — representative Vantera client environment" className="h-[460px] w-full object-cover lg:h-[560px]" loading="lazy" />
                        </div>
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Established infrastructure — where the opportunities already are</p>
                    </Reveal>
                    <div className="order-1 lg:order-2">
                        <Eyebrow>Best-fit profile</Eyebrow>
                        <Masked inview lines={["The Companies We're", "Built For."]} className="mt-7 font-display text-3xl font-extrabold tracking-tightest sm:text-5xl" />
                        <ul className="mt-9 space-y-4">
                            {PROFILE.map((p, i) => (
                                <Reveal key={p} delay={i * 0.05}>
                                    <li className="flex items-start gap-4 border-b border-ink/10 pb-4 text-[16.5px] font-medium text-ink">
                                        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-vantera-soft text-vantera" aria-hidden="true">
                                            <Check size={11} strokeWidth={3} />
                                        </span>
                                        {p}
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="border-y border-ink/10 bg-paper py-24 sm:py-32" data-testid="who-industries">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Eyebrow>Initial industry focus</Eyebrow>
                        <Masked inview lines={["Where We Operate First."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
                        {INDUSTRIES.map((ind, i) => (
                            <Reveal key={ind.name} delay={i * 0.07} className={i < 3 ? "lg:col-span-2" : "lg:col-span-3"}>
                                <div className="group relative h-64 overflow-hidden rounded-xl lg:h-72" data-testid={`industry-card-${i}`}>
                                    <img src={ind.img} alt={ind.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" aria-hidden="true" />
                                    <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-4 p-6">
                                        <h3 className="font-display text-lg font-extrabold tracking-tight text-ivory">{ind.name}</h3>
                                        <span className="font-mono text-[10px] text-vantera">{String(i + 1).padStart(2, "0")}</span>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 sm:py-32" data-testid="who-executives">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Eyebrow>Executives we work with</Eyebrow>
                        <Masked inview lines={["Operate Alongside Leadership."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <div className="mt-12 flex flex-wrap gap-3">
                        {EXECUTIVES.map((e, i) => (
                            <Reveal key={e} delay={i * 0.04}>
                                <span className="inline-flex items-center gap-3 rounded-full border border-ink/15 bg-white px-5 py-2.5 font-display text-[15px] font-bold tracking-tight text-ink">
                                    <span className="size-1.5 rounded-full bg-vantera" aria-hidden="true" /> {e}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CTASection heading={["If This Describes", "Your Business —", "Let's Talk."]} sub="See if Vantera fits your organization. The first conversation is an Executive Review." primary="See If Vantera Fits Your Organization" secondary="Book an Executive Review" testid="who-final-cta" />
        </>
    );
};

export default WhoWeHelp;